import React, { useMemo, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { AutoComplete, Input } from "antd";
import { useHistory } from "react-router-dom";
import { injectIntl } from "react-intl";
import { useSelector } from "react-redux";
import IntlMessage from "components/util-components/IntlMessage";
import Icon from "components/util-components/Icon";
import navigationConfig from "configs/NavigationConfig";
import { SIDE_NAV_WIDTH } from "constants/ThemeConstant";

const SIDE_NAV_SEARCH_POPUP_WIDTH = SIDE_NAV_WIDTH - 16;

function getOptionList(navigationTree, optionTree = []) {
	for (const navItem of navigationTree) {
		if (!navItem.submenu || navItem.submenu.length === 0) {
			if (navItem.path) {
				optionTree.push(navItem);
			}
		} else {
			getOptionList(navItem.submenu, optionTree);
		}
	}
	return optionTree;
}

function temPermissaoItem(item, listaPermissoes) {
	const p = item.permissao;
	if (!p) return true;
	const lista = Array.isArray(p) ? p : [p];
	return lista.some((codigo) => !codigo || listaPermissoes?.[codigo]);
}

function tituloOption(option, intl) {
	if (!option) return "";
	if (option.title) {
		try {
			return intl.formatMessage({ id: option.title });
		} catch (e) {
			return String(option.title);
		}
	}
	return String(option.value || "");
}

const SearchInput = (props) => {
	const {
		active,
		close,
		isMobile,
		mode,
		currentTheme = "light",
		variant = "header",
		intl,
	} = props;
	const isLight = currentTheme === "light" || mode === "light";
	const isSide = variant === "side";
	const history = useHistory();
	const listaPermissoes = useSelector((state) => state.permissoes);

	const optionList = useMemo(() => {
		const all = getOptionList(navigationConfig);
		return all.filter((item) => temPermissaoItem(item, listaPermissoes));
	}, [listaPermissoes]);

	const [value, setValue] = useState("");
	const [options, setOptions] = useState([]);
	const inputRef = useRef(null);

	const searchResult = (list) =>
		list.map((item) => ({
			value: item.path,
			title: item.title,
			label: (
				<div className="search-list-item d-flex align-items-center">
					{item.icon ? (
						<div className="icon mr-2" style={{ fontSize: "1.25rem" }}>
							<Icon type={item.icon} />
						</div>
					) : null}
					<div>
						<div className="font-weight-semibold">
							<IntlMessage id={item.title} />
						</div>
					</div>
				</div>
			),
		}));

	const filtrarOpcoes = (searchText) => {
		const termo = String(searchText || "")
			.trim()
			.toLowerCase();
		if (!termo) {
			return [];
		}
		return searchResult(optionList).filter((option) =>
			tituloOption(option, intl).toLowerCase().includes(termo),
		);
	};

	const irPara = (path) => {
		if (!path) {
			return;
		}
		history.push(path);
		setValue("");
		setOptions([]);
		if (close) {
			close();
		}
	};

	const onSelect = (path) => {
		irPara(path);
	};

	const onSearch = (searchText) => {
		setValue(searchText);
		setOptions(filtrarOpcoes(searchText));
	};

	const onPressEnter = () => {
		const filtradas = options.length ? options : filtrarOpcoes(value);
		if (!filtradas.length) {
			return;
		}
		const matchExato = filtradas.find((o) => o.value === value);
		irPara((matchExato || filtradas[0]).value);
	};

	if (active && inputRef.current) {
		inputRef.current.focus();
	}

	return (
		<div
			className={`${
				isSide ? "side-nav-search-input" : "header-nav-search"
			} ${
				isLight
					? isSide
						? "side-nav-search-input--light"
						: "header-nav-search--light"
					: isSide
						? "side-nav-search-input--dark"
						: "header-nav-search--dark"
			} ${!isMobile && !isSide ? "header-nav-search--wide" : ""}`}
		>
			<AutoComplete
				ref={inputRef}
				className={`nav-search-input ${isMobile ? "is-mobile" : ""}`}
				dropdownClassName={`nav-search-dropdown ${
					isLight
						? "nav-search-dropdown--light"
						: "nav-search-dropdown--dark"
				}`}
				style={{ width: "100%", minWidth: isSide ? undefined : 220 }}
				dropdownMatchSelectWidth={
					isSide ? SIDE_NAV_SEARCH_POPUP_WIDTH : true
				}
				options={options}
				onSelect={onSelect}
				onSearch={onSearch}
				value={value}
				defaultActiveFirstOption
				filterOption={false}
			>
				<Input
					placeholder="PESQUISAR"
					prefix={<SearchOutlined className="mr-0" />}
					allowClear
					onPressEnter={onPressEnter}
				/>
			</AutoComplete>
		</div>
	);
};

export default injectIntl(SearchInput);
